import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, Printer, FileSpreadsheet, Download, User, Globe, 
  Package, Calendar, Building2, Repeat, DollarSign, ArrowUpDown,
  ArrowUp, ArrowDown, Search, X
} from "lucide-react";
import { toast } from "sonner";
import { formatDMY } from "@/lib/dmy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerReportPage = () => {
  const { clientCode } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [clientInfo, setClientInfo] = useState(null);
  const [summary, setSummary] = useState(null);
  const printRef = useRef(null);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCustomers();
  }, [clientCode]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/crm/customers?client_code=${clientCode}&limit=1000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
        setSummary({
          total: data.total,
          repeat: data.customers?.filter(c => c.is_repeat).length || 0
        });
        
        // Calculate totals
        const totalValue = data.customers?.reduce((sum, c) => sum + (c.total_value || 0), 0) || 0;
        const totalOrders = data.customers?.reduce((sum, c) => sum + (c.total_orders || 0), 0) || 0;
        const countries = [...new Set(data.customers?.map(c => c.country).filter(Boolean))];
        
        setClientInfo({
          client_code: clientCode,
          total_customers: data.total,
          total_value: totalValue,
          total_orders: totalOrders,
          countries: countries
        });
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customer data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "₹0";
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon for column
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Parse date from various formats for sorting
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    // Handle DDMMYY format
    if (/^\d{6}$/.test(dateStr)) {
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const year = '20' + dateStr.substring(4, 6);
      return new Date(`${year}-${month}-${day}`);
    }
    // Handle formatted dates like "31 Jul 2025"
    return new Date(dateStr) || new Date(0);
  };

  // Filtered and sorted customers
  const filteredAndSortedCustomers = useMemo(() => {
    let result = [...customers];
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.customer_name || '').toLowerCase().includes(search) ||
        (c.country || '').toLowerCase().includes(search) ||
        (c.top_products?.[0]?.name || '').toLowerCase().includes(search) ||
        (c.categories?.join(' ') || '').toLowerCase().includes(search)
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortConfig.key) {
          case 'customer_name':
            aVal = (a.customer_name || '').toLowerCase();
            bVal = (b.customer_name || '').toLowerCase();
            break;
          case 'country':
            aVal = (a.country || '').toLowerCase();
            bVal = (b.country || '').toLowerCase();
            break;
          case 'total_orders':
            aVal = a.total_orders || 0;
            bVal = b.total_orders || 0;
            break;
          case 'total_value':
            aVal = a.total_value || 0;
            bVal = b.total_value || 0;
            break;
          case 'first_order':
            aVal = parseDate(a.first_order_formatted || a.first_order);
            bVal = parseDate(b.first_order_formatted || b.first_order);
            break;
          case 'last_order':
            aVal = parseDate(a.last_order_formatted || a.last_order);
            bVal = parseDate(b.last_order_formatted || b.last_order);
            break;
          case 'is_repeat':
            aVal = a.is_repeat ? 1 : 0;
            bVal = b.is_repeat ? 1 : 0;
            break;
          case 'top_product':
            aVal = (a.top_products?.[0]?.name || '').toLowerCase();
            bVal = (b.top_products?.[0]?.name || '').toLowerCase();
            break;
          default:
            return 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [customers, sortConfig, searchTerm]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    // Create CSV content
    const headers = ["S.No", "Customer Name", "Country", "Orders", "Total Value (INR)", "First Order", "Last Order", "Top Product", "Qty", "Categories", "Repeat"];
    
    const rows = customers.map((c, idx) => [
      idx + 1,
      c.customer_name,
      c.country,
      c.total_orders,
      c.total_value,
      c.first_order_formatted || c.first_order,
      c.last_order_formatted || c.last_order,
      c.top_products?.[0]?.name || "",
      c.top_products?.[0]?.count || "",
      c.categories?.join(", ") || "",
      c.is_repeat ? "Yes" : "No"
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${clientCode}_customers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("Excel file downloaded");
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-500">Loading customer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden on Print */}
      <div className="print:hidden bg-slate-50 border-b p-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Customer Report: {clientCode}
              </h1>
              <p className="text-sm text-slate-500">{summary?.total || 0} customers</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print / PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto p-6" ref={printRef}>
        {/* Report Header */}
        <div className="text-center mb-6 print:mb-4">
          <h1 className="text-2xl font-bold">Customer Report</h1>
          <p className="text-lg text-slate-600">Partner/Client: {clientCode}</p>
          <p className="text-sm text-slate-400">Generated on: {formatDMY(new Date())}</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6 print:grid-cols-4 print:gap-2 print:mb-4">
          <Card className="print:border print:shadow-none">
            <CardContent className="p-4 print:p-2">
              <div className="flex items-center gap-3">
                <User className="w-8 h-8 text-blue-500 print:w-6 print:h-6" />
                <div>
                  <p className="text-sm text-slate-500">Total Customers</p>
                  <p className="text-2xl font-bold print:text-lg">{clientInfo?.total_customers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="print:border print:shadow-none">
            <CardContent className="p-4 print:p-2">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-green-500 print:w-6 print:h-6" />
                <div>
                  <p className="text-sm text-slate-500">Total Orders</p>
                  <p className="text-2xl font-bold print:text-lg">{clientInfo?.total_orders || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="print:border print:shadow-none">
            <CardContent className="p-4 print:p-2">
              <div className="flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-amber-500 print:w-6 print:h-6" />
                <div>
                  <p className="text-sm text-slate-500">Total Value</p>
                  <p className="text-2xl font-bold print:text-lg">{formatCurrency(clientInfo?.total_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="print:border print:shadow-none">
            <CardContent className="p-4 print:p-2">
              <div className="flex items-center gap-3">
                <Repeat className="w-8 h-8 text-purple-500 print:w-6 print:h-6" />
                <div>
                  <p className="text-sm text-slate-500">Repeat Customers</p>
                  <p className="text-2xl font-bold print:text-lg">{summary?.repeat || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Table */}
        <Card className="print:border print:shadow-none">
          <CardHeader className="pb-2 print:pb-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg print:text-base">Customer List</CardTitle>
              <div className="flex items-center gap-2 print:hidden">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 w-64 h-8"
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {sortConfig.key && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSortConfig({ key: null, direction: 'asc' })}
                    className="text-red-600 h-8"
                  >
                    <X className="w-3 h-3 mr-1" /> Clear Sort
                  </Button>
                )}
              </div>
            </div>
            {(searchTerm || sortConfig.key) && (
              <p className="text-xs text-slate-500 mt-1 print:hidden">
                Showing {filteredAndSortedCustomers.length} of {customers.length} customers
                {sortConfig.key && ` • Sorted by ${sortConfig.key.replace('_', ' ')} (${sortConfig.direction})`}
              </p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm print:text-xs">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 print:p-1 font-medium">S.No</th>
                    <th 
                      className="text-left p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('customer_name')}
                    >
                      <div className="flex items-center">
                        Customer Name {getSortIcon('customer_name')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('country')}
                    >
                      <div className="flex items-center">
                        Country {getSortIcon('country')}
                      </div>
                    </th>
                    <th 
                      className="text-center p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('total_orders')}
                    >
                      <div className="flex items-center justify-center">
                        Orders {getSortIcon('total_orders')}
                      </div>
                    </th>
                    <th 
                      className="text-right p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('total_value')}
                    >
                      <div className="flex items-center justify-end">
                        Value {getSortIcon('total_value')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('first_order')}
                    >
                      <div className="flex items-center">
                        First Order {getSortIcon('first_order')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('last_order')}
                    >
                      <div className="flex items-center">
                        Last Order {getSortIcon('last_order')}
                      </div>
                    </th>
                    <th 
                      className="text-left p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('top_product')}
                    >
                      <div className="flex items-center">
                        Top Product {getSortIcon('top_product')}
                      </div>
                    </th>
                    <th className="text-left p-3 print:p-1 font-medium">Categories</th>
                    <th 
                      className="text-center p-3 print:p-1 font-medium cursor-pointer hover:bg-slate-100 select-none"
                      onClick={() => handleSort('is_repeat')}
                    >
                      <div className="flex items-center justify-center">
                        Repeat {getSortIcon('is_repeat')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedCustomers.map((customer, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="p-3 print:p-1">{idx + 1}</td>
                      <td className="p-3 print:p-1">
                        <span className="font-medium">{customer.customer_name}</span>
                      </td>
                      <td className="p-3 print:p-1">
                        <Badge variant="outline" className="bg-blue-50 print:text-[8px]">
                          {customer.country || "-"}
                        </Badge>
                      </td>
                      <td className="p-3 print:p-1 text-center font-medium">{customer.total_orders}</td>
                      <td className="p-3 print:p-1 text-right font-medium">{formatCurrency(customer.total_value)}</td>
                      <td className="p-3 print:p-1 text-slate-600">{customer.first_order_formatted || "-"}</td>
                      <td className="p-3 print:p-1">{customer.last_order_formatted || "-"}</td>
                      <td className="p-3 print:p-1">
                        {customer.top_products?.[0] && (
                          <span className="text-xs">
                            {customer.top_products[0].name} 
                            <Badge variant="secondary" className="ml-1 text-xs print:text-[8px]">
                              {customer.top_products[0].count}x
                            </Badge>
                          </span>
                        )}
                      </td>
                      <td className="p-3 print:p-1">
                        <div className="flex flex-wrap gap-1">
                          {customer.categories?.slice(0, 2).map((cat, i) => (
                            <Badge key={i} variant="outline" className="text-xs print:text-[8px]">{cat}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-3 print:p-1 text-center">
                        {customer.is_repeat && (
                          <Badge className="text-xs bg-green-100 text-green-700 print:text-[8px]">Yes</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredAndSortedCustomers.length === 0 && (
                    <tr>
                      <td colSpan={10} className="p-6 text-center text-slate-500">
                        {searchTerm ? "No customers match your search" : "No customers found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-slate-400 print:mt-4">
          <p>Report generated by EAC CRM</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          @page { margin: 1cm; size: A4 landscape; }
        }
      `}</style>
    </div>
  );
};

export default CustomerReportPage;
